import { ExclamationCircleOutlined, LoadingOutlined } from '@ant-design/icons';
import type { ExamType, StudentGroup } from '@evidia/shared';
import { STUDENT_GROUP_TEXT } from '@evidia/shared';
import { Checkbox, Modal, Spin, Tooltip } from 'antd';
import { useState } from 'react';
import styled from 'styled-components';
import { useActiveSchoolId } from '../hooks/useActiveSchoolId';
import { useAppConfig } from '../hooks/useAppConfig';
import { getExamTry, getPrevExam } from '../utils/exam';
import { trpc } from '../utils/trcp';
import ExampType from './ExampType';

const { confirm } = Modal;

const Wrapper = styled.div`
	display: flex;
`;

const Tag = styled.span<{ $margin?: boolean }>`
	font-weight: bold;
	margin-left: ${({ $margin }) => ($margin ? '1em' : '0')};
`;

const CheckBoxWrapper = styled.div<{ $isMobileView?: boolean }>`
	padding-right: ${({ $isMobileView }) => ($isMobileView ? '1em' : '1em')};
	padding: ${({ $isMobileView }) => ($isMobileView ? '1em' : '0')};
`;

interface Props {
	forceDisable?: boolean;
	studentId: string;
	examId: string;
	type: ExamType;
	group: StudentGroup;
	studentOnExam: any[];
	logs: any[];
	exams: any[];
	refetch?: () => Promise<void>;
	shouwGroupInTitle?: boolean;
	isMobileView?: boolean;
}

const ExampCheckBox: React.FC<Props> = ({
	refetch,
	forceDisable,
	group,
	studentId,
	examId,
	type,
	studentOnExam,
	exams,
	shouwGroupInTitle = false,
	isMobileView = false,
}) => {
	const config = useAppConfig();

	const { activeSchoolId } = useActiveSchoolId();

	const targetExam = studentOnExam.find(
		v =>
			v.examId === examId &&
			(v.type as unknown as ExamType) === (type as unknown as ExamType) &&
			v.group === group,
	);
	const [isChecked, setChecked] = useState<boolean>(!!targetExam);
	const [loading, setLoading] = useState(false);

	const { mutateAsync: addStudentToExam } = trpc.exam.addStudentToExam.useMutation();

	const handleOnClick = async (checked: boolean): Promise<void> => {
		// const { checked } = v.target;

		if (checked === true) {
			confirm({
				icon: <ExclamationCircleOutlined />,
				// title: 'Opravdu chcete odstranit zkoušku?',
				content: 'Opravdu chcete žáka odhlásit ze zkoušky?',
				// okType: 'warning',
				okText: 'Ano',
				cancelText: 'Ne',
				onOk: async () => {
					setLoading(true);
					await addStudentToExam({
						schoolId: activeSchoolId,
						examId,
						studentId,
						toRemoveType: {
							type,
							group,
						},
					});
					setChecked(false);

					await refetch?.();
					setLoading(false);
				},
			});
		} else {
			setLoading(true);
			await addStudentToExam({
				schoolId: activeSchoolId,
				examId,
				studentId,
				newType: {
					type,
					group,
				},
			});

			setChecked(true);
			await refetch?.();
			setLoading(false);
		}
	};

	const counter = getExamTry(exams as any, { type, studentOnExam: studentOnExam as any, examId });
	const prevExam = getPrevExam(exams as any, { type, studentOnExam: studentOnExam as any });

	const prevExams = exams.filter(e => {
		if (isChecked) {
			return false;
		}

		if (e.id === examId) {
			return false;
		}

		const isTypeMatch = e.type.includes(type as any);
		if (!isTypeMatch) {
			return false;
		}

		// const studentOnExamRes = studentOnExam.find(v => v.examId === e.id && v.type.includes(type));
		// if (studentOnExamRes) {
		// 	if (studentOnExamRes.results[type] === ExamResult.Fail) {
		// 		return false;
		// 	}

		// 	return !studentOnExamRes.results[type];
		// }

		return false;
	});

	const hasPrevExam = prevExams.length > 0 || forceDisable;

	if (loading) {
		return (
			<Wrapper>
				<CheckBoxWrapper $isMobileView={isMobileView}>
					<Spin indicator={<LoadingOutlined style={{ fontSize: 20 }} spin />} />
					{shouwGroupInTitle && `(${STUDENT_GROUP_TEXT[group]}) `}
					<Tag $margin>
						<ExampType tag={false} type={type} />
					</Tag>
				</CheckBoxWrapper>
			</Wrapper>
		);
	}

	if (hasPrevExam) {
		return (
			<Wrapper>
				<CheckBoxWrapper $isMobileView={isMobileView}>
					<Tooltip
						title={
							forceDisable
								? 'Před přihlášením na zkoušku, ukončete výcvik studenta'
								: 'Student je přihlášen už na jiný termín'
						}
					>
						<Checkbox onChange={() => handleOnClick(isChecked)} checked={isChecked} disabled={hasPrevExam}>
							{forceDisable ? (
								<>
									{shouwGroupInTitle && `(${STUDENT_GROUP_TEXT[group]}) `}
									<ExampType tag={false} type={type} />
									{/* <Tag>{` ${prevExam?.date}` ?? 'error'}</Tag> */}
								</>
							) : (
								<>
									{shouwGroupInTitle && `(${STUDENT_GROUP_TEXT[group]}) `}
									<ExampType tag={false} type={type} />
									<Tag>{` ${prevExam?.date ?? 'error'}`}</Tag>
								</>
							)}
						</Checkbox>
					</Tooltip>
				</CheckBoxWrapper>
			</Wrapper>
		);
	}

	return (
		<Wrapper>
			<CheckBoxWrapper $isMobileView={isMobileView}>
				<Checkbox onChange={() => handleOnClick(isChecked)} checked={isChecked} disabled={hasPrevExam}>
					<Tag>
						{shouwGroupInTitle && `(${STUDENT_GROUP_TEXT[group]}) `}
						<ExampType tag={false} type={type} />

						{isChecked && ` (${counter}/${config.maximumNumberOfAttemptsPerExam})`}
					</Tag>
				</Checkbox>
			</CheckBoxWrapper>
		</Wrapper>
	);
};

export default ExampCheckBox;
